import React, { Fragment, h } from "preact";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "preact/hooks";
import { F, Card } from "../../../_common/layout";
import { t } from "../../../_i18n";
import Banner from "../../../_common/components/Banner";
import {
  downloadDocument,
  getDocuments,
  uploadDocument,
} from "../../../_rx/documentos/actions";

const MAX_FILE_SIZE = 5;
const UPLOAD_DOCUMENT_TYPE_ID = 146;
const DOWNLOAD_DOCUMENT_TYPE_ID = 142;
const SECOND_DOWNLOAD_DOCUMENT_TYPE_ID = 27;
const PENDING_STATUS_ID = 1;
const REJECTED_STATUS_IDS = [4];
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const getFileError = (file) => {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return t("Solo se permiten archivos PDF o DOC/DOCX");
  }

  if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
    return t("El archivo no puede superar los 5 MB");
  }

  return null;
};

const getDocumentForProgram = (documents, typeId, academicProgramId, allowUnscoped = false) =>
  (documents || []).find(
    (document) =>
      Number(document?.type?.id) === typeId &&
      (Number(document?.academic_program_id) === academicProgramId ||
        (allowUnscoped && document?.academic_program_id == null)),
  );

const getStatusName = (document) =>
  (document?.status?.name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isRejectedDocument = (document) => {
  const statusName = getStatusName(document);

  return (
    REJECTED_STATUS_IDS.includes(Number(document?.status?.id)) ||
    statusName.includes("rechaz")
  );
};

function CertificateApplicationNew({ academicProgram, selectedAcademicProgramId }) {
  const dispatch = useDispatch();
  const studentId = useSelector((state) => state.auth?.loggedUser?.id);
  const studentIdentityDocument = useSelector(
    (state) => state.status?.student_meta?.student_identity_document,
  );
  const academicProgramId = Number(
    selectedAcademicProgramId ?? academicProgram?.id,
  );
  const documents = useSelector((state) => state.documents?.documents);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (studentId) dispatch(getDocuments(studentId));
  }, [studentId]);

  const uploadedDocument = getDocumentForProgram(
    documents,
    UPLOAD_DOCUMENT_TYPE_ID,
    academicProgramId,
    true,
  );
  const rejectedDocument = (documents || []).find(
    (document) =>
      Number(document?.type?.id) === UPLOAD_DOCUMENT_TYPE_ID &&
      Number(document?.academic_program_id) === academicProgramId &&
      isRejectedDocument(document),
  );
  const validatedDocument = getDocumentForProgram(
    documents,
    DOWNLOAD_DOCUMENT_TYPE_ID,
    academicProgramId,
  );
  const secondValidatedDocument = getDocumentForProgram(
    documents,
    SECOND_DOWNLOAD_DOCUMENT_TYPE_ID,
    academicProgramId,
  );
  const uploadedDocumentStatus = Number(uploadedDocument?.status?.id);
  const uploadedDocumentStatusName = getStatusName(uploadedDocument);
  const isValidating = !rejectedDocument && !validatedDocument && (
    uploadedDocumentStatus === PENDING_STATUS_ID ||
    uploadedDocumentStatusName === "validando" ||
    uploadedDocumentStatusName.includes("validacion") ||
    (uploadedDocumentStatusName.includes("pendiente") && uploadedDocumentStatusName.includes("valid"))
  );
  const isRejected = Boolean(rejectedDocument);
  const hasUploadedDocument = Boolean(uploadedDocument && !isRejected);
  const hasFile = Boolean(file || hasUploadedDocument);
  const fileName = file?.name || (hasUploadedDocument && uploadedDocument?.name);
  const rejectionMessage = (rejectedDocument?.errors || [])
    .map((error) => error?.custom_error_message)
    .filter(Boolean)
    .join(" ");

  const isUploadDocumentValidated = !isValidating && (
    uploadedDocumentStatusName.includes("validado") ||
    uploadedDocumentStatusName.includes("finalizado") ||
    [2, 3, 4].includes(uploadedDocumentStatus)
  );
  const areDownloadDocumentsReady =
    Boolean(validatedDocument && secondValidatedDocument);
  const isWaitingForDownloadDocuments =
    !isRejected &&
    hasUploadedDocument &&
    !areDownloadDocumentsReady &&
    (isUploadDocumentValidated || validatedDocument || secondValidatedDocument);

  const handleFileChange = (event) => {
    if (isValidating) return;

    const selected = event.target.files?.[0];

    if (!selected) {
      setFile(null);
      setFileError(null);
      return;
    }

    const error = getFileError(selected);
    setFileError(error);

    if (error) {
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const handleUploadFile = async () => {
    if (!file || !studentId || !academicProgramId || isValidating) return;

    setIsUploading(true);

    try {
      await Promise.resolve(
        dispatch(
          uploadDocument(studentId, UPLOAD_DOCUMENT_TYPE_ID, file, {
            academicProgramId,
          }),
        ),
      );
    } catch (error) {
      setFileError(t("No se ha podido enviar el archivo"));
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  if (!academicProgram || !Number.isFinite(academicProgramId)) {
    return (
      <p className="t-center">
        <em className="disabled-aspect-gray">{t("Cargando")}. . .</em>
      </p>
    );
  }

  if (documents == null) {
    return (
      <p className="t-center">
        <em className="disabled-aspect-gray">{t("Cargando")}. . .</em>
      </p>
    );
  }

  if (isWaitingForDownloadDocuments) {
    return (
      <F className="max-width-980 m-x2-top gap-2 flex flex-col">
        <p
          dangerouslySetInnerHTML={{
            __html: t("certificate_management_validated_pending_download"),
          }}
        />
      </F>
    );
  }

  if (!isRejected && areDownloadDocumentsReady) {
    const [validatedInfoBeforeLink, validatedInfoAfterLink] = t(
      "certificate_management_validated_info",
    ).split("document_links");

    return (
      <F className="max-width-980 m-x2-top gap-2 flex flex-col">
        <p>
          <span dangerouslySetInnerHTML={{ __html: validatedInfoBeforeLink }} />
          <ul className="list-disc pl-6">
            <li>
              <a
                href="#"
                className="font-color-149BAB t-underline"
                onClick={(event) => {
                  event.preventDefault();
                  downloadDocument(
                    studentId,
                    validatedDocument.id,
                    validatedDocument.name,
                  );
                }}
              >
                {t("certificate_download_payment_receipt")}
              </a>
            </li>
            <li>
              <a
                href="#"
                className="font-color-149BAB t-underline"
                onClick={(event) => {
                  event.preventDefault();
                  downloadDocument(
                    studentId,
                    secondValidatedDocument.id,
                    secondValidatedDocument.name,
                  );
                }}
              >
                {t("certificate_download_grades_certificate")}
              </a>
            </li>
          </ul>
          <span dangerouslySetInnerHTML={{ __html: validatedInfoAfterLink }} />
        </p>
      </F>
    );
  }

  return (
    <F className="max-width-980 m-x2-top gap-2 flex flex-col">
      <p>{t("certificate_management_congratulations")}</p>
      <p className="my-4">
        {t("certificate_management_upload_intro")} {t("certificate_management_validation_intro")} <strong>{t("certificate_management_validation_time")}</strong>
      </p>
      <p><strong>{t("certificate_management_physical_title")}</strong></p>
        {(!uploadedDocument || isRejected) && (
        <Fragment>
          <div className="mt-4" dangerouslySetInnerHTML={{ __html: t("certificate_management_payment_info") }} />
          <Banner color="warning" background="warning" icon="warning" className="mt-2 -mb-3">
            <span
              dangerouslySetInnerHTML={{
                __html: t("certificate_management_identity_document_info").replace(
                  "[X]",
                  studentIdentityDocument || "—",
                ),
              }}
            />
          </Banner>
        </Fragment>
      )}
      <Card smOnly={false} className="mt-8">
        <p className="font-bold m-bottom mt-2">{t("certificate_management_payment_receipt")}</p>
        {!isValidating && (
          <div className="flex flex-col gap-3 rounded-lg py-3">
            <div>
              <p className="mb-1 font-bold">{t("Archivo")}</p>
              <div
                onClick={() => !isUploading && fileRef.current?.click()}
                className={`border border-dashed border-gray-300 p-6 text-center ${isUploading ? "cursor-not-allowed opacity-60" : "hover:bg-info-50 cursor-pointer"}`}
              >
                {isUploading ? t("Enviando archivo...") : hasFile ? fileName : <span className="font-bold"><i className="fa fa-upload" /> {t("Seleccionar archivo")}</span>}
              </div>
              <input ref={fileRef} type="file" className="hidden" disabled={isUploading} onChange={handleFileChange} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
              <p className="mt-2 text-xs">
                {hasUploadedDocument && !file ? t("Documento ya enviado") : null}
                {fileError ? <span className="text-error-600">{fileError}</span> : t("PDF o DOC (máx. 5 MB)")}
              </p>
            </div>
          </div>
        )}
        {isRejected && (
          <Banner color="error" background="error" icon="exclamation-triangle" className="mt-2 mb-4">
            <span
              dangerouslySetInnerHTML={{
                __html: t("El resguardo del pago de tasas ha sido rechazado.")
              }}
            />
            {rejectionMessage && (
              <p className="mt-2">
                <strong>{t("Motivo del rechazo")}:</strong> {rejectionMessage}
              </p>
            )}
          </Banner>
        )}
        {hasUploadedDocument && (
          <Banner color="info" background="info" icon="info-circle" className="mt-2 mb-4">
            <span
              dangerouslySetInnerHTML={{
                __html: t("El archivo se ha subido correctamente y está siendo revisado.")
              }}
            />
          </Banner>
        )}
        {file && (!hasUploadedDocument || isUploading) && (
          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="btn btn-primary inline-flex items-center gap-2"
              disabled={isUploading || !academicProgramId}
              onClick={handleUploadFile}
              aria-busy={isUploading}
            >
              {isUploading && (
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden="true"
                />
              )}
              {isUploading ? t("Enviando archivo...") : t("Enviar archivo")}
            </button>
          </div>
        )}
      </Card>
    </F>
  );
}

export default CertificateApplicationNew;
